import React, { useState, useEffect, useRef } from 'react';
import { Attachment, attachmentApi } from '../services/api';
import { MediaImage, MediaVideo, Page, Folder, Attachment as AttachIcon, Download, Trash, Plus, Xmark } from 'iconoir-react';

interface AttachmentListProps {
  cardId: number;
  canEdit: boolean;
}

export const AttachmentList: React.FC<AttachmentListProps> = ({ cardId, canEdit }) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadAttachments();
  }, [cardId]);

  const loadAttachments = async () => {
    setIsLoading(true);
    try {
      const response = await attachmentApi.getAll(cardId);
      setAttachments(response.data);
      setError(null);
    } catch (err: any) {
      setError('Erreur lors du chargement des fichiers');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    // Vérifier la taille (50MB max)
    if (file.size > 50 * 1024 * 1024) {
      setError('Le fichier est trop volumineux (50MB maximum)');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      const response = await attachmentApi.upload(cardId, file);
      setAttachments([response.data, ...attachments]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err: any) {
      setError('Erreur lors de l\'upload du fichier');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (attachment: Attachment) => {
    try {
      const response = await attachmentApi.download(cardId, attachment.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Erreur lors du téléchargement');
    }
  };

  const handleImagePreview = async (attachment: Attachment) => {
    try {
      const response = await attachmentApi.download(cardId, attachment.id);
      const blob = new Blob([response.data], { type: attachment.mime_type });
      const url = window.URL.createObjectURL(blob);
      setPreviewImage(url);
    } catch (err: any) {
      setError('Erreur lors du chargement de l\'image');
    }
  };

  const closePreview = () => {
    if (previewImage) {
      window.URL.revokeObjectURL(previewImage);
      setPreviewImage(null);
    }
  };

  const handleDelete = async (attachmentId: number) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce fichier ?')) {
      return;
    }

    try {
      await attachmentApi.delete(cardId, attachmentId);
      setAttachments(attachments.filter(a => a.id !== attachmentId));
    } catch (err: any) {
      setError('Erreur lors de la suppression');
    }
  };

  const getFileIcon = (fileType: string) => {
    const iconProps = { width: 24, height: 24, strokeWidth: 2 };
    switch (fileType) {
      case 'image':
        return <MediaImage {...iconProps} color="#0079BF" />;
      case 'video':
        return <MediaVideo {...iconProps} color="#6554C0" />;
      case 'document':
        return <Page {...iconProps} color="#FF5630" />;
      case 'archive':
        return <Folder {...iconProps} color="#FF991F" />;
      default:
        return <AttachIcon {...iconProps} color="#5E6C84" />;
    }
  };

  return (
    <div className="attachment-section">
      <div className="attachment-header">
        <h3>
          <AttachIcon width={20} height={20} strokeWidth={2} style={{ display: 'inline', marginRight: '8px' }} />
          Pièces jointes ({attachments.length})
        </h3>
        {canEdit && (
          <button
            className="btn btn-attachment-add"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <Plus width={16} height={16} strokeWidth={2} />
            <span>{isUploading ? 'Upload en cours...' : 'Ajouter un fichier'}</span>
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          disabled={isUploading}
        />
      </div>

      {error && <div className="attachment-error">{error}</div>}

      {isLoading ? (
        <div className="attachment-loading">Chargement...</div>
      ) : attachments.length === 0 ? (
        <div className="attachment-empty">Aucune pièce jointe</div>
      ) : (
        <div className="attachment-list">
          {attachments.map((attachment) => (
            <div key={attachment.id} className="attachment-item">
              <div 
                className="attachment-icon"
                onClick={() => {
                  if (attachment.file_type === 'image') {
                    handleImagePreview(attachment);
                  }
                }}
                style={{ cursor: attachment.file_type === 'image' ? 'pointer' : 'default' }}
                title={attachment.file_type === 'image' ? 'Cliquez pour prévisualiser' : ''}
              >
                {getFileIcon(attachment.file_type)}
              </div>
              <div className="attachment-info">
                <div className="attachment-filename" title={attachment.filename}>
                  {attachment.filename}
                </div>
                <div className="attachment-meta">
                  {attachment.formatted_size} • Ajouté par {attachment.user.name}
                </div>
              </div>
              <div className="attachment-actions">
                <button
                  className="btn-attachment-download"
                  onClick={() => handleDownload(attachment)}
                  title="Télécharger"
                >
                  <Download width={18} height={18} strokeWidth={2} />
                </button>
                {canEdit && (
                  <button
                    className="btn-attachment-delete"
                    onClick={() => handleDelete(attachment.id)}
                    title="Supprimer"
                  >
                    <Trash width={18} height={18} strokeWidth={2} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Modale de prévisualisation d'image */}
      {previewImage && (
        <div className="image-preview-backdrop" onClick={closePreview}>
          <div className="image-preview-container" onClick={(e) => e.stopPropagation()}>
            <button className="image-preview-close" onClick={closePreview}>
              <Xmark width={20} height={20} strokeWidth={2} />
            </button>
            <img src={previewImage} alt="Prévisualisation" className="image-preview" />
          </div>
        </div>
      )}
    </div>
  );
};
